import { useState, useEffect, useRef } from 'react';

// ── Rate cache: base USD, fetched once per session ────────────────────────────
let ratesCache = null;
let ratesFetchPromise = null;

export async function getAllRates() {
  if (ratesCache) return ratesCache;
  if (ratesFetchPromise) return ratesFetchPromise;
  ratesFetchPromise = fetch('https://api.exchangerate-api.com/v4/latest/USD')
    .then(r => r.json())
    .then(data => {
      ratesCache = data?.rates || { GTQ: 7.75, EUR: 0.92 };
      return ratesCache;
    })
    .catch(() => {
      ratesCache = { GTQ: 7.75, EUR: 0.92, GBP: 0.79, JPY: 149.5, MXN: 17.1, CAD: 1.36, AUD: 1.53 };
      return ratesCache;
    });
  return ratesFetchPromise;
}

export function toUSD(amount, fromCurrency, rates) {
  if (!rates || fromCurrency === 'USD') return amount;
  const rate = rates[fromCurrency];
  if (!rate) return amount;
  return amount / rate;
}

export function useAllRates() {
  const [rates, setRates] = useState(ratesCache || null);
  useEffect(() => { getAllRates().then(setRates); }, []);
  return rates;
}

// Backwards-compat: old GTQ-only hook still works
export function useExchangeRate() {
  const rates = useAllRates();
  return rates ? (1 / (rates['GTQ'] || 7.75)) : 0.13;
}

const POPULAR = [
  'GTQ', 'USD', 'EUR', 'GBP', 'JPY', 'MXN', 'CAD', 'AUD',
  'BRL', 'COP', 'HNL', 'CRC', 'NIO', 'PEN', 'CLP',
  'CHF', 'CNY', 'INR', 'KRW', 'SGD', 'QAR', 'AED', 'ZAR',
];

/**
 * CurrencyInput — amount field with any-currency selector + live USD conversion
 *
 * Props:
 *   value           – raw string value typed by the user
 *   onChange        – called with (rawValue, usdValue, currency)
 *   placeholder     – input placeholder
 *   style           – extra container style
 *   defaultCurrency – starting currency (default: 'USD')
 */
export default function CurrencyInput({
  value,
  onChange,
  placeholder = '0.00',
  style = {},
  defaultCurrency = 'GTQ',
}) {
  const [currency, setCurrency] = useState(defaultCurrency);
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState('');
  const rates = useAllRates();
  const pickerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowPicker(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getUSD = (raw, cur) => {
    const num = parseFloat(raw);
    if (isNaN(num) || !rates) return 0;
    return parseFloat(toUSD(num, cur, rates).toFixed(2));
  };

  const handleAmountChange = (e) => {
    const raw = e.target.value;
    onChange(raw, getUSD(raw, currency), currency);
  };

  const handleCurrencySelect = (newCur) => {
    setCurrency(newCur);
    setShowPicker(false);
    setSearch('');
    onChange(value, getUSD(value, newCur), newCur);
  };

  const usdPreview = (() => {
    if (!rates || currency === 'USD') return null;
    const num = parseFloat(value);
    if (isNaN(num) || num === 0) return null;
    const usd = toUSD(num, currency, rates);
    const rate = rates[currency];
    return {
      usd: usd.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }),
      rate: rate ? `1 ${currency} = ${(1 / rate).toFixed(4)} USD` : null,
    };
  })();

  const allCurrencies = rates ? Object.keys(rates).sort() : POPULAR;
  const filtered = search
    ? (() => {
        const q = search.toLowerCase();
        const matches = allCurrencies.filter(c => c.toLowerCase().includes(q));
        // Always keep GTQ and USD at top if they match
        const priority = ['GTQ', 'USD'].filter(c => matches.includes(c));
        const rest = matches.filter(c => !priority.includes(c));
        return [...priority, ...rest];
      })()
    : POPULAR;

  const badgeColor = {
    USD: { bg: '#1a2a3a', fg: '#60a5fa' },
    GTQ: { bg: '#1a3a2a', fg: '#4ade80' },
    EUR: { bg: '#2a1a3a', fg: '#c084fc' },
    GBP: { bg: '#3a2a1a', fg: '#fb923c' },
  };
  const badge = badgeColor[currency] || { bg: '#2a2a2a', fg: '#e2e8f0' };

  return (
    <div style={{ position: 'relative', ...style }}>
      <div style={{ display: 'flex' }}>
        <input
          type="number"
          step="0.01"
          min="0"
          placeholder={placeholder}
          value={value}
          onChange={handleAmountChange}
          style={{
            flex: 1,
            padding: '9px 12px',
            border: '1px solid var(--stone)',
            borderRadius: '6px 0 0 6px',
            borderRight: 'none',
            fontSize: 13,
            fontFamily: 'var(--font-b)',
            outline: 'none',
            background: 'white',
            color: 'var(--ink)',
            boxSizing: 'border-box',
          }}
        />
        <button
          type="button"
          onClick={() => { setShowPicker(p => !p); setSearch(''); }}
          style={{
            border: '1px solid var(--stone)',
            borderRadius: '0 6px 6px 0',
            background: badge.bg,
            color: badge.fg,
            fontWeight: 700,
            fontSize: 11,
            padding: '0 10px',
            cursor: 'pointer',
            minWidth: 58,
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: 3,
          }}
        >
          {currency} <span style={{ fontSize: 9, opacity: 0.6 }}>▾</span>
        </button>
      </div>

      {/* Live USD preview */}
      {usdPreview && (
        <div style={{ fontSize: 10.5, color: 'var(--dim)', marginTop: 4, paddingLeft: 2, lineHeight: 1.4 }}>
          ≈ <strong style={{ color: 'var(--ink2)' }}>{usdPreview.usd}</strong>
          {usdPreview.rate && (
            <span style={{ marginLeft: 6, color: 'var(--mid)' }}>({usdPreview.rate})</span>
          )}
        </div>
      )}

      {/* Dropdown Picker */}
      {showPicker && (
        <div
          ref={pickerRef}
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            zIndex: 200,
            background: 'white',
            border: '1px solid var(--stone)',
            borderRadius: 8,
            boxShadow: '0 8px 28px rgba(0,0,0,0.13)',
            width: 230,
          }}
        >
          <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--stone)' }}>
            <input
              autoFocus
              type="text"
              placeholder="Search currency (e.g. EUR, GBP)…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid var(--stone)',
                borderRadius: 5,
                fontSize: 12,
                fontFamily: 'var(--font-b)',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ maxHeight: 240, overflowY: 'auto', padding: '5px 0' }}>
            {!search && (
              <div style={{
                fontSize: 9.5, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.1em', color: 'var(--mid)',
                padding: '4px 14px 3px',
              }}>
                Popular
              </div>
            )}
            {filtered.map(cur => {
              const rate = rates?.[cur];
              const rateLabel = rate && cur !== 'USD'
                ? (1 / rate) < 0.01
                  ? (1 / rate).toExponential(2)
                  : (1 / rate).toFixed(4)
                : null;
              const isSelected = cur === currency;
              return (
                <div
                  key={cur}
                  onClick={() => handleCurrencySelect(cur)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '7px 14px',
                    cursor: 'pointer',
                    fontSize: 12.5,
                    fontWeight: isSelected ? 600 : 400,
                    background: isSelected ? 'var(--off)' : 'transparent',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--off)'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span>{cur}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {rateLabel && (
                      <span style={{ fontSize: 10.5, color: 'var(--mid)' }}>{rateLabel}</span>
                    )}
                    {isSelected && <span style={{ color: 'var(--green)', fontSize: 12 }}>✓</span>}
                  </span>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--dim)' }}>
                No currency found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
